class AlgoliaInstantSearch extends HTMLElement {
  constructor() {
    super();

    this.afterISStartRemoveDefaultWidget = this.afterISStartRemoveDefaultWidget.bind(this);
    this.beforeInstantSearchAllowParamsArray = this.beforeInstantSearchAllowParamsArray.bind(this);
    this.beforeInstantSearchMainTemplate = this.beforeInstantSearchMainTemplate.bind(this);
    this.beforeInstantSearchOptions = this.beforeInstantSearchOptions.bind(this);
    this.beforeISStartAddWidgetArray = this.beforeISStartAddWidgetArray.bind(this);
    this.connectedCallback = this.connectedCallback.bind(this);
    this.onMount = this.onMount.bind(this);
    this.setupWidget = this.setupWidget.bind(this);
    this.setupWidgets  = this.setupWidgets.bind(this);
    this.subscribe = this.subscribe.bind(this);
  }

  /**
   * Removes default widgets after instant search has started.
   * @param {Array} defaultWidgets - The array of default widgets to filter.
   * @returns {Array} An array of widgets with specific types filtered out.
   */
  afterISStartRemoveDefaultWidget(defaultWidgets) {
    const widgetTypes = ['ais.hits', 'ais.pagination', 'ais.sortBy'];
    const filteredWidgets = defaultWidgets.filter(widget => widgetTypes.includes(widget.$$widgetType));
    return filteredWidgets;
  }

  /**
   * Concatenates query params to the given allowed params.
   * If allowParams is an array, it adds query params to it. If it is a single value, it creates a new array with the value and then adds query params.
   * @param {(Array|string)} allowParams - The array or value to which query params will be added.
   * @returns {Array} A new array containing the allowd params.
   */
  beforeInstantSearchAllowParamsArray(allowParams) {
    try {
      const href = window.location.href;
      const url = new URL(href);
      const search = new URLSearchParams(url.search);
      const params = Array.from(search.keys());
      return params;
    } catch (e) {
      console.error('Error', e);
      const defaultParams = [].concat(
        allowParams,
        "icid",
        "tid",
        "utm_campaign",
        "utm_content",
        "utm_medium",
        "utm_source",
        "utm_term",
      );
      return defaultParams;
    }
  }

  /**
   * Generates the main template for the instant search before the default template.
   * @param {Object} defaultTemplate - The default template.
   * @param {Object} data - Data used for generating the template.
   * @param {Function} html - Function for generating HTML elements.
   * @returns {HTMLElement} - The generated HTML template.
   */
  beforeInstantSearchMainTemplate(_defaultTemplate, data, html) {
    return html`
      <div class="template-container">
        <div class="ais-section-padding viewport-spacing template-main-collection">
          <div class="ais-input hidden">
            <div class="ais-input-button"></div>
            <div class="ais-clear-input-icon"></div>
            <div class="ais-search-box-container"></div>
          </div>
          <div class="ais-clear-refinements-container hidden"></div>
          <div class="ais-search-header hidden">
            <div class="ais-change-display">
              <span class="ais-change-display-block ais-change-display-selected"></span>
              <span class="ais-change-display-list"></span>
            </div>
          </div>
          <div class="ais-facets-button hidden"></div>
          <div class="ais-hits-container hidden"></div>
          <div class="ais-pagination-container hidden"></div>
          ${data.facets.map(facet => html`<div class="ais-facet-${facet.escapedName}-container hidden"></div>`)}
          ${this.statsDetails.mobile.renderContainer(html)}
          <div class="facets facets-vertical-sort small-hide medium-hide no-js-hidden">
            <form class="facets-vertical-form">
              ${this.statsDetails.desktop.renderContainer(html)}
              ${this.sortBy.desktop.renderContainer(html)}
              ${this.stats.desktop.renderContainer(html)}
            </form>
          </div>
          <div class="facets-vertical">
            <aside class="facets-wrapper" id="main-collection-filters">
              <div class="facets-container">
                <facet-filters-form class="facets facets small-hide medium-hide">
                  <form class="facets__form-vertical">
                    <div id="FacetsWrapperDesktop">
                      <div class="active-facets active-facets-desktop">
                        ${this.clearRefinements.desktop.renderContainer(html)}
                        ${this.currentRefinements.desktop.renderContainer(html)}
                      </div>
                    </div>
                    ${this.refinementList.desktop.map(desktop => desktop.renderContainer(html))}
                  </form>
                </facet-filters-form>
                ${this.stats.mobile.renderContainer(html)}
                ${this.showCurrentRefinements.mobile.renderContainer(html)}
              </div>
            </aside>
            <div class="ais-facets facets-container large-up-hide mobile-facets__main">
              ${this.sortBy.mobile.renderContainer(html)}
              ${this.clearRefinements.mobile.renderContainer(html)}
              ${this.currentRefinements.mobile.renderContainer(html)}
              ${this.refinementList.mobile.map(mobile => mobile.renderContainer(html))}
            </div>
            ${this.infiniteHits.desktop.renderContainer(html)}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Modifies the instant search options before initialization.
   * @param {Object} options - instant search options object.
   * @returns {Object} Modified instant search options object.
   */
  beforeInstantSearchOptions(options) {
    options.hitsPerPage = this.settings.productsPerPage;
    return options;
  }

  /**
   * Creates and returns an array of widgets to be added before instant search starts.
   * @returns {Array} Array of widgets.
   */
  beforeISStartAddWidgetArray() {
    const clearRefinementsDesktop = this.clearRefinements.desktop.createWidget();
    const clearRefinementsMobile = this.clearRefinements.mobile.createWidget();
    const currentRefinementsDesktop = this.currentRefinements.desktop.createWidget();
    const currentRefinementsMobile = this.currentRefinements.mobile.createWidget();
    const infiniteHitsDesktop = this.infiniteHits.desktop.createWidget();
    const showCurrentRefinementsMobile = this.showCurrentRefinements.mobile.createWidget();
    const sortByDesktop = this.sortBy.desktop.createWidget();
    const sortByMobile = this.sortBy.mobile.createWidget();
    const statsDesktop = this.stats.desktop.createWidget();
    const statsMobile = this.stats.mobile.createWidget();
    const statsDetailsDesktop = this.statsDetails.desktop.createWidget();
    const statsDetailsMobile = this.statsDetails.mobile.createWidget();
    const refinementListsDesktop = this.refinementList.desktop.map(desktop => desktop.createWidget());
    const refinementListsMobile = this.refinementList.mobile.map(mobile => mobile.createWidget());

    return [
      clearRefinementsDesktop,
      clearRefinementsMobile,
      currentRefinementsDesktop,
      currentRefinementsMobile,
      infiniteHitsDesktop,
      showCurrentRefinementsMobile,
      sortByDesktop,
      sortByMobile,
      statsDesktop,
      statsMobile,
      statsDetailsDesktop,
      statsDetailsMobile,
      ...refinementListsDesktop,
      ...refinementListsMobile,
    ];
  }

  /**
   * Initializes settings based on custom attributes when the element is connected to the DOM.
   * @returns {void}
   */
  connectedCallback() {
    const rawPropertiesToGroup = this.getAttribute('properties-to-group') || '';
    const propertiesToGroup = rawPropertiesToGroup
      .split(',')
      .map(prop => prop.trim())
      .filter(prop => prop.length > 0);

    this.settings = {
      productsPerPage: this.getAttribute('products-per-page'),
      enableGroupingFilter: this.getAttribute('enable-grouping-filter') === 'true',
      propertiesToGroup
    };
    this.onMount();
  }

  /**
   * Registers an event listener for the 'algolia.hooks.initialize' event when the component mounts.
   * @returns {void}
   */
  onMount() {
    document.addEventListener('algolia.hooks.initialize', this.subscribe);
  }

  /**
   * Sets up a widget using the provided container, mobile flag, selector, and additional configuration.
   * @param {HTMLElement} container - The container element where the widget will be placed.
   * @param {boolean} isMobile - Indicates if the widget is being rendered on a mobile device.
   * @param {string} selector - CSS selector to find the widget element within the container.
   * @param {object} rest - Additional configuration options for the widget.
   * @returns {object|null} Object with methods to create and render the widget or null if the selector does not match any element.
   */
  setupWidget(container, isMobile, selector, rest) {
    const configuration = { container, isMobile, ...rest };
    const instances = this.instances;

    const element = this.querySelector(selector);
    if (!element) return null;

    return ({
      createWidget: () => element.createWidget(configuration, instances),
      renderContainer: (html) => element.renderContainer(configuration, html),
    });
  }

  /**
   * Initializes and configures all algolia widgets used in the application.
   * @returns {void}
   */
  setupWidgets() {
    this.clearRefinements = {
      desktop: this.setupWidget('ais-clear-current-refinements-container', false, 'algolia-clear-refinements'),
      mobile: this.setupWidget('ais-clear-current-refinements-container-mobile', true, 'algolia-clear-refinements')
    };

    this.currentRefinements = {
      desktop: this.setupWidget('ais-current-refined-values-container', false, 'algolia-current-refinements'),
      mobile: this.setupWidget('ais-current-refined-values-container-mobile', true, 'algolia-current-refinements')
    };

    let infiniteHitsConfig = {};

    if (this.settings.enableGroupingFilter) {
      infiniteHitsConfig = {
        transformItems: buildTransformItems(this.settings.propertiesToGroup)
      };
    }

    this.infiniteHits = {
      desktop: this.setupWidget(
        'ais-infinite-hits-container',
        false,
        'algolia-infinite-hits',
        infiniteHitsConfig,
      )
    };

    this.showCurrentRefinements = {
      mobile: this.setupWidget('ais-show-current-refined-values-container-mobile', true, 'algolia-show-current-refinements')
    };

    this.sortBy = {
      desktop: this.setupWidget('ais-sort-orders-container', false, 'algolia-sort-by'),
      mobile: this.setupWidget('ais-sort-orders-container-mobile', true, 'algolia-sort-by')
    };

    this.stats = {
      desktop: this.setupWidget('ais-stats-container', false, 'algolia-stats'),
      mobile: this.setupWidget('ais-stats-container-mobile', true, 'algolia-stats')
    };

    this.statsDetails = {
      desktop: this.setupWidget('ais-details-container', false, 'algolia-stats-details'),
      mobile: this.setupWidget('ais-details-container-mobile', true, 'algolia-stats-details')
    };

    /* When we are working with a specific collection, we want to use that collection's facets rather than the default, which is general. */
    const currentCollectionId = this.instances.algoliaShopify.current_collection_id;
    const collectionFacets = this.instances.algoliaShopify.config.collection_facets;
    let currentCollectionFacets;
    if (currentCollectionId) {
      currentCollectionFacets = collectionFacets[currentCollectionId] || collectionFacets["default"];
    } else {
      currentCollectionFacets = this.instances.algoliaShopify.config.facets;
    }
    let filteredCollectionFacets = [];
    if (currentCollectionFacets)  {
      filteredCollectionFacets = currentCollectionFacets.filter(collectionFacet => collectionFacet.type !== 'hidden');
    }

    this.refinementList = {
      desktop: filteredCollectionFacets.reduce((previous, current) => {
        const selector = current.type === 'slider' ? 'algolia-range-input' : 'algolia-refinement-list';
        const widget = this.setupWidget(current.name, false, selector, current);
        return current.enabled ? [...previous, widget] : previous;
      }, []),
      mobile: filteredCollectionFacets.reduce((previous, current) => {
        const selector = current.type === 'slider' ? 'algolia-range-input' : 'algolia-refinement-list';
        const widget = this.setupWidget(current.name, true, selector, current);
        return current.enabled ? [...previous, widget] : previous;
      }, [])
    };
  }

  /**
   * Subscribes to algolia hooks and sets up necessary configurations.
   * @returns {void}
   */
  subscribe() {
    this.instances = { algoliaShopify: window.algoliaShopify, instantSearch: window.instantsearch };
    if (!this.instances.algoliaShopify || !this.instances.instantSearch) return;

    this.setupWidgets();

    this.instances.algoliaShopify.hooks.registerHook('afterISStartRemoveDefaultWidget', this.afterISStartRemoveDefaultWidget);
    this.instances.algoliaShopify.hooks.registerHook('beforeInstantSearchAllowParamsArray', this.beforeInstantSearchAllowParamsArray);
    this.instances.algoliaShopify.hooks.registerHook('beforeInstantSearchMainTemplate', this.beforeInstantSearchMainTemplate);
    this.instances.algoliaShopify.hooks.registerHook('beforeInstantSearchOptions', this.beforeInstantSearchOptions);
    this.instances.algoliaShopify.hooks.registerHook('beforeISStartAddWidgetArray', this.beforeISStartAddWidgetArray);
  };
}

const getValueByPath = (obj, path) => {
  try {
    return Function('obj', `return obj?.${path}`)(obj);
  } catch (e) {
    return undefined;
  }
}

/**
 * Returns a transformItems function that filters out duplicates from an item list
 * based on a set of property paths (e.g., 'title', 'sku').
 *
 * If no properties are provided, the original list is returned unmodified.
 * Otherwise, the function constructs a key for each item by combining the values
 * of the specified paths, and removes any items that share the same key.
 *
 * @param {string[]} properties - List of property paths used to build the deduplication key.
 * @returns {(items: object[]) => object[]} - A filtering function to deduplicate the item list.
 */
const buildTransformItems = (properties = []) => {
  if (!Array.isArray(properties) || properties.length === 0) {
    return items => items;
  }

  return (items) => {
    const seen = new Set();
    return items.filter(item => {
      const keyParts = properties.map(path => getValueByPath(item, path));
      if (keyParts.some(part => part === undefined)) return true;

      const key = keyParts.join('||').toLowerCase();
      if (seen.has(key)) return false;

      seen.add(key);
      return true;
    });
  }
}

customElements.define("algolia-instant-search", AlgoliaInstantSearch);
